/**
 * Firmware — Network > Firmware (inventory + rollout status).
 *
 * Two read surfaces over the existing firmware models: the org's uploaded firmware
 * IMAGES (per device_type, stable flag, size, notes) and recent UPDATE attempts with
 * their live status/progress. Upload + scheduling are gated actions handled elsewhere;
 * this is the inventory + rollout dashboard.
 */
import { useState } from "react";
import { fmtDate } from "@/lib/format";
import {
  useFirmwareImages,
  useFirmwareUpdates,
  useRegisterFirmwareUrl,
  type FirmwareImage,
  type FirmwareUpdate,
} from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

/** Map a firmware update status to the operational ramp. */
function statusTone(s: string | null): "healthy" | "warn" | "critical" | "info" {
  if (s === "completed") return "healthy";
  if (s === "failed") return "critical";
  if (s === "in_progress" || s === "downloading" || s === "applying" || s === "pending") return "warn";
  return "info";
}

function fmtSize(bytes: number): string {
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
  return `${bytes} B`;
}

export default function Firmware() {
  const images = useFirmwareImages();
  const updates = useFirmwareUpdates();
  const imgRows = images.data?.items ?? [];
  const updRows = updates.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <PageHeader title="Firmware upgrades" subtitle="Firmware images and rollout status across this customer" />
        {images.error && <ErrorBanner error={images.error} />}
        {images.isLoading && <div className="text-sm text-ink-muted">Loading images…</div>}

        {images.data && imgRows.length === 0 && (
          <EmptyState
            title="No firmware images"
            hint="Register a vendor-hosted image by URL (below) or upload a binary per device model. Onboarding then auto-brings new devices to the baseline (see the customer's firmware policy)."
          />
        )}

        {imgRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-ink-subtle">
                <tr className="[&>th]:py-1.5 [&>th]:pr-4 [&>th]:font-medium">
                  <th>Version</th><th>Device model</th><th>Channel</th><th>Source</th><th>Size</th><th>Added</th>
                </tr>
              </thead>
              <tbody>
                {imgRows.map((f) => (
                  <tr key={f.id} className="border-t border-line/60 [&>td]:py-2 [&>td]:pr-4">
                    <td className="font-mono font-medium text-ink">{f.version}</td>
                    <td className="text-ink-muted">{f.device_type}</td>
                    <td>
                      <span className={`rounded px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide ${f.is_stable ? "bg-healthy-soft text-healthy" : "bg-surface-2 text-ink-subtle"}`}>
                        {f.is_stable ? "stable" : "pre-release"}
                      </span>
                    </td>
                    <td><SourceChip f={f} /></td>
                    <td className="font-mono text-ink-muted">{f.file_size ? fmtSize(f.file_size) : "—"}</td>
                    <td className="text-ink-muted">{fmtDate(f.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Register a vendor-hosted image (TIP JFrog etc.) -- no upload, the AP fetches it directly. */}
        <RoleGate min="org_admin" mode="hide">
          <AddImageByUrl />
        </RoleGate>
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold text-ink">Rollout status</h2>
        {updates.error && <ErrorBanner error={updates.error} />}
        {updates.isLoading && <div className="text-sm text-ink-muted">Loading updates…</div>}

        {updates.data && updRows.length === 0 && (
          <EmptyState title="No firmware updates yet" hint="Scheduled and in-flight firmware updates appear here with live progress." />
        )}

        {updRows.length > 0 && (
          <ul className="flex flex-col gap-2">
            {updRows.map((u) => <UpdateRow key={u.id} u={u} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Where the bytes live: a vendor URL (shown by host, full URL on hover) or a controller upload. */
function SourceChip({ f }: { f: FirmwareImage }) {
  if (f.source !== "url" || !f.url) {
    return <span className="text-xs text-ink-muted">Upload</span>;
  }
  let host = f.url;
  try { host = new URL(f.url).host; } catch { /* keep raw */ }
  return (
    <span className="text-xs text-ink-muted" title={f.url}>
      Vendor · <span className="font-mono">{host}</span>
    </span>
  );
}

/**
 * Add image by URL — register a vendor-hosted firmware image as a managed baseline.
 *
 * The device model must match exactly what the devices report (e.g. "Edgecore EAP111"): that
 * string is what the auto-baseline executor matches on. The AP fetches the image straight from
 * the URL, so no controller download host is needed. 409 = that version already exists.
 */
function AddImageByUrl() {
  const register = useRegisterFirmwareUrl();
  const [form, setForm] = useState({
    device_type: "", version: "", url: "", sha256: "", release_notes: "", is_stable: true,
  });
  const [done, setDone] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const ready = form.device_type.trim() && form.version.trim() && /^https?:\/\//i.test(form.url.trim());

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setDone(null);
    register.mutate(
      {
        device_type: form.device_type.trim(),
        version: form.version.trim(),
        url: form.url.trim(),
        sha256: form.sha256.trim() || undefined,
        release_notes: form.release_notes.trim() || undefined,
        is_stable: form.is_stable,
      },
      {
        onSuccess: (img) => {
          setDone(`Registered ${img.version} for ${img.device_type}.`);
          setForm((f) => ({ ...f, version: "", url: "", sha256: "", release_notes: "" }));
        },
      },
    );
  };

  const err = register.error;
  const errText =
    err instanceof ApiError && err.status === 409
      ? "That version already exists for this model."
      : err
        ? err.message || "Could not register the image."
        : null;

  const inCls = "min-w-0 rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-brand";

  return (
    <form onSubmit={submit} className="md-card mt-4 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-ink">Add image by URL</h3>
        <p className="mt-0.5 text-xs text-ink-muted">
          Vendor-hosted image (e.g. a TIP release on JFrog). The device model must match what the
          devices report — that string is what auto-baseline matches on.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <input className={inCls} placeholder="Device model, e.g. Edgecore EAP111" value={form.device_type} onChange={set("device_type")} />
        <input className={inCls} placeholder="Version, e.g. 4.2.7" value={form.version} onChange={set("version")} />
        <input className={`${inCls} sm:col-span-2 font-mono text-xs`} placeholder="https://…-upgrade.bin" value={form.url} onChange={set("url")} />
        <input className={`${inCls} font-mono text-xs`} placeholder="sha256 (optional)" value={form.sha256} onChange={set("sha256")} />
        <input className={inCls} placeholder="Release notes (optional)" value={form.release_notes} onChange={set("release_notes")} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-ink">
          <input type="checkbox" checked={form.is_stable} onChange={set("is_stable")} />
          Stable channel (the baseline onboarding uses)
        </label>
        <Button variant="primary" type="submit" disabled={!ready || register.isPending}>
          {register.isPending ? "Registering…" : "Register"}
        </Button>
        {done && <span className="text-xs text-healthy">{done}</span>}
        {errText && <span className="text-xs text-critical">{errText}</span>}
      </div>
    </form>
  );
}

function UpdateRow({ u }: { u: FirmwareUpdate }) {
  return (
    <li className="md-card flex items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{u.device_name ?? u.device_serial ?? `device #${u.device_id}`}</span>
          {u.version && <span className="font-mono text-xs text-ink-muted">→ {u.version}</span>}
        </div>
        <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-surface-2">
          <div className="h-full rounded-full bg-brand" style={{ width: `${Math.min(100, Math.max(0, u.progress))}%` }} />
        </div>
        {u.error_message && <div className="mt-1 text-xs text-critical">{u.error_message}</div>}
      </div>
      <StatusPill status={statusTone(u.status)}>{u.status ?? "unknown"}</StatusPill>
    </li>
  );
}
